/**
 * Filtro + ordenação das TARPs FECHADAS (GUPM-188 — painel de auditoria no M3).
 * TS puro. Ordenação por severidade usa o NÍVEL EFETIVO (GUPM-207): uma fechada
 * que escalou a EMERGÊNCIA (caminho atingiu `paebm`) tem precedência sobre uma
 * de nível de catálogo mais brando, mesmo sendo mais antiga.
 */
import type { TarpLevel } from '../types';
import type { FlowState } from './flow';
import { nivelEfetivo } from './nivel-efetivo';

export type OrdenacaoFechadas = 'recente' | 'severidade';

export interface FiltroFechadas {
  /** Código do TARP, ou 'todos'/ausente = todos os tipos. */
  tipo?: string;
  /** Código da barragem (A/B/C), ou 'todas'/ausente = todas. */
  damCode?: string;
  ordenar: OrdenacaoFechadas;
}

/** Shape mínimo para filtrar/ordenar (desacopla do TarpRecord). */
export interface FechadaLike {
  tarpCode: string;
  damCode: string;
  closedAt: string | null;
  flowState: FlowState | null;
}

const SEV_ORDER: Record<TarpLevel, number> = { NORMAL: 0, ATENCAO: 1, ALERTA: 2, EMERGENCIA: 3 };

/** `closedAt` desc (mais recente primeiro); `null` por último (linha malformada). */
function compareClosedDesc(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? 1 : -1;
}

/**
 * Aplica filtros (tipo, barragem) e ordenação (recente | severidade) às fechadas.
 * `tipo`/`damCode` ausentes ou 'todos'/'todas' não recortam. Não muta a entrada.
 */
export function filtrarOrdenarFechadas<T extends FechadaLike>(
  records: readonly T[],
  filtro: FiltroFechadas,
): T[] {
  const semTipo = !filtro.tipo || filtro.tipo === 'todos';
  const semDam = !filtro.damCode || filtro.damCode === 'todas';
  const filtered = records.filter(
    (r) => (semTipo || r.tarpCode === filtro.tipo) && (semDam || r.damCode === filtro.damCode),
  );
  const sorted = [...filtered];
  if (filtro.ordenar === 'severidade') {
    sorted.sort((a, b) => {
      const sev = SEV_ORDER[nivelEfetivo(b.tarpCode, b.flowState)] - SEV_ORDER[nivelEfetivo(a.tarpCode, a.flowState)];
      return sev !== 0 ? sev : compareClosedDesc(a.closedAt, b.closedAt); // desempate por recente
    });
  } else {
    sorted.sort((a, b) => compareClosedDesc(a.closedAt, b.closedAt));
  }
  return sorted;
}
